/**
 * Share links.
 *
 * A shared route is encoded entirely into the URL fragment. The fragment is
 * never sent to a server by the browser, so sharing needs no backend, works on
 * static hosting, and leaks nothing to the host of the page.
 *
 * Wire format (before base64url):
 *
 *   byte 0        container: 0x01 = raw payload, 0x02 = gzip payload
 *   bytes 1..n    payload
 *
 * Payload:
 *
 *   varint        schema version (currently 1)
 *   string        route name
 *   varint        waypoint count
 *   n x {                                     positions, delta encoded
 *     zigzag varint   latitude  delta, units of 1e-5 degrees
 *     zigzag varint   longitude delta, units of 1e-5 degrees
 *   }
 *   varint        number of named waypoints
 *   m x {
 *     varint      waypoint index
 *     string      name
 *   }
 *
 * where `string` is a varint byte-length followed by UTF-8.
 *
 * 1e-5 degrees is about 1.1 m of latitude — far finer than any GPS fix and finer
 * than the charts themselves, while keeping deltas small enough that a typical
 * coastal waypoint costs 3-5 bytes.
 */

import { isValidLatLon } from './geo';
import type { Route, Waypoint } from './route';

/** Schema version written into the payload. Bump on any incompatible change. */
export const SHARE_SCHEMA_VERSION = 1;

const CONTAINER_RAW = 0x01;
const CONTAINER_GZIP = 0x02;

/** Coordinate quantisation: 1e-5 degrees. */
const COORD_SCALE = 1e5;

/**
 * Practical URL ceiling. Browsers and chat apps vary, but links beyond roughly
 * this length start getting truncated by messaging clients, which is worse than
 * refusing: a truncated route decodes as a *different* route.
 */
export const MAX_SAFE_SHARE_URL_LENGTH = 2000;

export class ShareDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShareDecodeError';
  }
}

// --- Primitive encoding -----------------------------------------------------

class ByteWriter {
  private bytes: number[] = [];

  varint(value: number): void {
    if (!Number.isInteger(value) || value < 0) throw new RangeError(`varint requires a non-negative integer, got ${value}`);
    let v = value;
    while (v >= 0x80) {
      this.bytes.push((v & 0x7f) | 0x80);
      v = Math.floor(v / 128);
    }
    this.bytes.push(v);
  }

  /** ZigZag maps signed to unsigned so small negatives stay small. */
  zigzag(value: number): void {
    if (!Number.isInteger(value)) throw new RangeError(`zigzag requires an integer, got ${value}`);
    this.varint(value < 0 ? -2 * value - 1 : 2 * value);
  }

  string(value: string): void {
    const encoded = new TextEncoder().encode(value);
    this.varint(encoded.length);
    for (const b of encoded) this.bytes.push(b);
  }

  toUint8Array(): Uint8Array {
    return new Uint8Array(this.bytes);
  }
}

class ByteReader {
  private offset = 0;

  constructor(private readonly bytes: Uint8Array) {}

  get exhausted(): boolean {
    return this.offset >= this.bytes.length;
  }

  varint(): number {
    let result = 0;
    let shift = 1;
    for (;;) {
      if (this.offset >= this.bytes.length) throw new ShareDecodeError('truncated varint');
      const byte = this.bytes[this.offset++];
      result += (byte & 0x7f) * shift;
      if ((byte & 0x80) === 0) break;
      shift *= 128;
      if (shift > Number.MAX_SAFE_INTEGER) throw new ShareDecodeError('varint overflow');
    }
    return result;
  }

  zigzag(): number {
    const v = this.varint();
    return v % 2 === 0 ? v / 2 : -(v + 1) / 2;
  }

  string(): string {
    const length = this.varint();
    if (this.offset + length > this.bytes.length) throw new ShareDecodeError('truncated string');
    const slice = this.bytes.subarray(this.offset, this.offset + length);
    this.offset += length;
    return new TextDecoder().decode(slice);
  }
}

// --- base64url --------------------------------------------------------------

const B64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    out += B64URL_ALPHABET[b0 >> 2];
    out += B64URL_ALPHABET[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)];
    if (b1 === undefined) break;
    out += B64URL_ALPHABET[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)];
    if (b2 === undefined) break;
    out += B64URL_ALPHABET[b2 & 0x3f];
  }
  return out;
}

export function fromBase64Url(text: string): Uint8Array {
  const values: number[] = [];
  for (const char of text) {
    const index = B64URL_ALPHABET.indexOf(char);
    if (index === -1) throw new ShareDecodeError(`invalid base64url character: ${JSON.stringify(char)}`);
    values.push(index);
  }
  const bytes: number[] = [];
  for (let i = 0; i < values.length; i += 4) {
    const [v0, v1, v2, v3] = [values[i], values[i + 1], values[i + 2], values[i + 3]];
    if (v1 === undefined) throw new ShareDecodeError('invalid base64url length');
    bytes.push((v0 << 2) | (v1 >> 4));
    if (v2 === undefined) break;
    bytes.push(((v1 & 0x0f) << 4) | (v2 >> 2));
    if (v3 === undefined) break;
    bytes.push(((v2 & 0x03) << 6) | v3);
  }
  return new Uint8Array(bytes);
}

// --- Compression ------------------------------------------------------------

const hasCompressionStream = (): boolean => typeof globalThis.CompressionStream === 'function';

async function gzipBytes(input: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([input as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function gunzipBytes(input: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([input as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// --- Route <-> payload ------------------------------------------------------

export function encodePayload(route: Pick<Route, 'name' | 'waypoints'>): Uint8Array {
  const writer = new ByteWriter();
  writer.varint(SHARE_SCHEMA_VERSION);
  writer.string(route.name);
  writer.varint(route.waypoints.length);

  let previousLat = 0;
  let previousLon = 0;
  for (const waypoint of route.waypoints) {
    if (!isValidLatLon(waypoint)) throw new RangeError('route contains an invalid position');
    // Quantise against the running total, not the raw previous coordinate, so
    // rounding cannot accumulate across a long route.
    const lat = Math.round(waypoint.lat * COORD_SCALE);
    const lon = Math.round(waypoint.lon * COORD_SCALE);
    writer.zigzag(lat - previousLat);
    writer.zigzag(lon - previousLon);
    previousLat = lat;
    previousLon = lon;
  }

  const named = route.waypoints
    .map((waypoint, index) => ({ index, name: waypoint.name }))
    .filter((entry): entry is { index: number; name: string } => Boolean(entry.name));
  writer.varint(named.length);
  for (const entry of named) {
    writer.varint(entry.index);
    writer.string(entry.name);
  }

  return writer.toUint8Array();
}

export interface DecodedRoute {
  name: string;
  waypoints: Array<{ lat: number; lon: number; name?: string }>;
}

export function decodePayload(bytes: Uint8Array): DecodedRoute {
  const reader = new ByteReader(bytes);

  const version = reader.varint();
  if (version !== SHARE_SCHEMA_VERSION) {
    throw new ShareDecodeError(
      `unsupported share format v${version} — this link was made by a newer version of the app`,
    );
  }

  const name = reader.string();
  const count = reader.varint();
  if (count > 10_000) throw new ShareDecodeError('waypoint count is implausibly large');

  const waypoints: DecodedRoute['waypoints'] = [];
  let lat = 0;
  let lon = 0;
  for (let i = 0; i < count; i++) {
    lat += reader.zigzag();
    lon += reader.zigzag();
    const position = { lat: lat / COORD_SCALE, lon: lon / COORD_SCALE };
    if (!isValidLatLon(position)) {
      throw new ShareDecodeError(`waypoint ${i + 1} decodes to an impossible position`);
    }
    waypoints.push(position);
  }

  // A truncated link often still parses this far, so the named-waypoint block is
  // treated as optional but its contents are validated strictly.
  if (!reader.exhausted) {
    const namedCount = reader.varint();
    for (let i = 0; i < namedCount; i++) {
      const index = reader.varint();
      const waypointName = reader.string();
      if (index >= waypoints.length) throw new ShareDecodeError('waypoint name refers to a missing waypoint');
      waypoints[index].name = waypointName;
    }
  }

  return { name, waypoints };
}

// --- Public API -------------------------------------------------------------

/** Encode a route to the fragment token (no leading "#"). */
export async function encodeRouteToToken(route: Pick<Route, 'name' | 'waypoints'>): Promise<string> {
  const payload = encodePayload(route);

  let container = CONTAINER_RAW;
  let body = payload;
  if (hasCompressionStream()) {
    const compressed = await gzipBytes(payload);
    // gzip has ~20 bytes of framing; on a two-waypoint route that loses.
    if (compressed.length < payload.length) {
      container = CONTAINER_GZIP;
      body = compressed;
    }
  }

  const framed = new Uint8Array(body.length + 1);
  framed[0] = container;
  framed.set(body, 1);
  return toBase64Url(framed);
}

export async function decodeTokenToRoute(token: string): Promise<DecodedRoute> {
  const framed = fromBase64Url(token.trim());
  if (framed.length < 2) throw new ShareDecodeError('share link is empty or truncated');

  const container = framed[0];
  const body = framed.subarray(1);

  let payload: Uint8Array;
  if (container === CONTAINER_RAW) {
    payload = body;
  } else if (container === CONTAINER_GZIP) {
    if (typeof globalThis.DecompressionStream !== 'function') {
      throw new ShareDecodeError('this browser cannot read compressed share links');
    }
    try {
      payload = await gunzipBytes(body);
    } catch {
      throw new ShareDecodeError('share link is corrupt — it may have been truncated when shared');
    }
  } else {
    throw new ShareDecodeError(`unrecognised share link format (0x${container.toString(16)})`);
  }

  return decodePayload(payload);
}

/** Full shareable URL for a route, built against the app's own location. */
export async function buildShareUrl(
  route: Pick<Route, 'name' | 'waypoints'>,
  baseUrl: string,
): Promise<string> {
  const token = await encodeRouteToToken(route);
  const url = new URL(baseUrl);
  url.hash = `route=${token}`;
  return url.toString();
}

/** Extract a share token from a URL or bare fragment, or null if absent. */
export function extractShareToken(input: string): string | null {
  const hashIndex = input.indexOf('#');
  const fragment = hashIndex === -1 ? input : input.slice(hashIndex + 1);
  const params = new URLSearchParams(fragment);
  return params.get('route');
}

/**
 * Whether a link is long enough to risk truncation by messaging apps. The UI
 * should warn and offer to trim the route rather than silently emitting a link
 * that will arrive broken.
 */
export function isShareUrlRisky(url: string): boolean {
  return url.length > MAX_SAFE_SHARE_URL_LENGTH;
}

/** Convert a decoded share into waypoints with fresh local identifiers. */
export function decodedToWaypoints(decoded: DecodedRoute, makeId: (index: number) => string): Waypoint[] {
  return decoded.waypoints.map((waypoint, index) => ({
    id: makeId(index),
    lat: waypoint.lat,
    lon: waypoint.lon,
    ...(waypoint.name === undefined ? {} : { name: waypoint.name }),
  }));
}
